import {
  Accordion,
  AccordionPanel,
  Anchor,
  Box,
  Button,
  DateInput,
  FileInput,
  FormField,
  Header,
  Heading,
  Image,
  Layer,
  NameValueList,
  NameValuePair,
  Page,
  PageContent,
  Text,
  TextArea,
  TextInput,
  RadioButtonGroup,
  CheckBox,
} from "grommet";
import { Next, StatusWarning } from "grommet-icons";
import { fromUrl } from "parse-domain";
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BSON } from "realm-web";
import { useImmer } from "use-immer";
import { okayActor, okayDomain } from "../utils/validate";
import { LoadingIndicator } from "./LoadingIndicator";
import { RealmContext } from "./providers/RealmProvider";
import { AbuseFormConfig, ClientConfigContext } from "./wrappers/ClientWrapper";
import { FormElement } from "./FormElement";
import { CheckStatus } from "./CheckStatus";
import { phone } from 'phone';
import { useEmbedded } from "../hooks/useEmbedded";

interface AbuseType {
  id: string;
  class: "Content" | "Activity";
  type: string;
  subType?: string;
  label: string;
}

export interface AbuseGroups {
  heading: string;
  preText: string | ReactNode;
  examples: (string | ReactNode)[];
  postText: string | ReactNode;
  types: string[];
}

interface AbuseReport {
  phase: number;
  client: string;
  abuse: {
    type: AbuseType | undefined;
    date: Date;
    ongoing: boolean;
    description: string;
  };
  details: {
    target?: string;
    headers?: string;
    body?: string;
    filename?: string;
    malware?: string;
    md5?: string;
    sha1?: string;
    smsNumber?: string;
    url?: string
    copyrightHolder?: string;
    copyrightDescription?: string;
    copyrightAcknowledgement?: boolean;
    copyrightSignature?: string;
    copyrightSworn?: boolean;
  };
  reporter: {
    realmId: string;
    email: string;
    fullName?: string;
    phoneNumber?: string;
  };
  actor: {
    url: string;
    okay: boolean;
  };
  evidence: File[];
}

interface Receipt {
  okay: Boolean;
  error: string | undefined;
  _id: BSON.ObjectId | null;
}

const abuseTypes: AbuseType[] = [
  {
    id: "csam",
    class: "Content",
    type: "CSAM",
    label: "Child Sexual Abuse Material",
  },
  {
    id: "nocsam",
    class: "Content",
    type: "CSAM",
    label: "Child Sexual Abuse Material",
  },
  {
    id: "violence",
    class: "Content",
    type: "Violence",
    label: "Violent or disturbing imagery, excluding CSAM",
  },
  {
    id: "pharmaceutical",
    class: "Content",
    type: "Pharmaceutical",
    label: "Suspicious pharmaceutical content",
  },
  {
    id: "pii",
    class: "Content",
    type: "PIIExposure",
    label: "Exposure of personal information",
  },
  {
    id: "malware",
    class: "Content",
    type: "Malware",
    label: "Virus or malicious software",
  },
  {
    id: "cracking",
    class: "Content",
    type: "Cracking",
    label: "Promotion of computer or network hacking",
  },
  {
    id: "phishing",
    class: "Content",
    type: "Phishing",
    label: "Email or web site requesting personal information",
  },
  {
    id: "pharming",
    class: "Content",
    type: "Phishing",
    subType: "Pharming",
    label: "Web site posing as another web site",
  },
  {
    id: "spam",
    class: "Activity",
    type: "Spam",
    subType: "Email",
    label: "Unsolicited email",
  },
  {
    id: "sms",
    class: "Activity",
    type: "Spam",
    subType: "SMS",
    label: "Unsolicited text message",
  },
  {
    id: "comment",
    class: "Activity",
    type: "Spam",
    subType: "Online",
    label: "Unsolicited online comment",
  },
  {
    id: "copyright",
    class: "Content",
    type: "Copyright",
    label: "Copyright infringement",
  },
  { id: "botnet", class: "Content", type: "Botnet", label: "Botnet activity" },
  {
    id: "ddos",
    class: "Content",
    type: "DDoS",
    label: "Distributed denial of service (DDos)",
  },
  {
    id: "loginattack",
    class: "Content",
    type: "LoginAttack",
    label: "Login Attack",
  },
];

const bucket = import.meta.env.VITE_STORAGE_BUCKET;

const phishingRadioOptions = ['Yes', 'No'];
const porkbunHostingRadioOptions = ['Yes', 'No'];

export function NewReport() {
  const [localAbuseTypes, setLocalAbuseTypes] = useState(abuseTypes);
  const [phishingOption, setTrademarkOption] = useState<string | undefined>(undefined);
  const [porkbunHosting, setPorkbunHosting] = useState<string | undefined>(undefined);
  const config = useContext(ClientConfigContext);
  const navigate = useNavigate();
  const { isEmbedded } = useEmbedded();
  const { user, mongoClient } = useContext(RealmContext);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const initialReport: AbuseReport = useMemo(() => ({
    phase: 1,
    client: config.code,
    abuse: {
      type: undefined,
      date: new Date(),
      ongoing: true,
      description: "",
    },
    details: {},
    reporter: {
      realmId: user!.id,
      email: "",
    },
    actor: {
      url: "",
      okay: false,
    },
    evidence: [],
  }), [config.code, user]);

  const [mode, setMode] = useState<"report" | "status">("report");

  const [receipt, setReceipt] = useState<Receipt>({
    okay: false,
    error: "",
    _id: null,
  });
  const clearError = useCallback(() => setReceipt((r) => ({ ...r, error: "" })), []);

  const [errors, setErrors] = useImmer<Record<string, string | false>>({});
  const [report, setReport] = useImmer<AbuseReport>(initialReport);
  const hasErrors = useMemo(
    () => Boolean(Object.values(errors).find(Boolean)) ||
      (config.code === 'porkbun' && report.abuse.type?.id === 'dmca' && (!porkbunHosting || porkbunHosting === 'No')),
    [errors, porkbunHosting, config.code, report.abuse.type?.id]
  );
  const resetReport = useCallback(() => {
    setReport((draft) => {
      return { ...initialReport, reporter: draft.reporter };
    });
    setReceipt({ okay: false, error: "", _id: null });
    navigate(`/${config.code}`);
  }, [initialReport]);

  const setReportType = useCallback(
    (type: AbuseType) => {
      setReport((draft) => {
        draft.abuse.type = type;
      });
    },
    [setReport]
  );

  const submitReport = useCallback(async () => {
    if (hasErrors || !report.abuse.type) {
      setReceipt({
        okay: false,
        error: "Please correct the errors in your report",
        _id: null,
      });
      return;
    }

    const captcha = await new Promise((pass) => {
      // @ts-ignore
      window.grecaptcha.enterprise.ready(async () => {
        // @ts-ignore
        const token = await window.grecaptcha.enterprise.execute(
          "6Lcl2cAbAAAAAKwwFmX9syDSQmvtWVYdnNTzVjuF",
          { action: "submit_report" }
        );
        const result = await user!.functions
          .verifyRecaptcha({ token, expectedAction: "submit_report" })
          .catch((error) => ({ success: false, response: error.toString() }));
        pass(result);
      });
    });

    setSubmitting(true);
    const ip = await fetch("https://api.ipify.org").then((r) => r.text());
    const _id = new BSON.ObjectId();

    const hostname = fromUrl(report.actor.url) as string;
    const evidence: string[] = [];

    for (let i = 0; i < report.evidence.length; i++) {
      const body = report.evidence[i];
      const ext = body.name.replace(/.*\./, "");
      const key = `evidence/${hostname}-${_id}-${i}.${ext}`;

      await user!.functions
        .getSignedUrl({ key, bucket })
        .then((putUrl: string) =>
          fetch(putUrl, {
            method: "PUT",
            body,
          })
        )
        .then(
          () =>
            // user!.functions.getSignedUrl({ key, bucket, operation: "getObject" })
            `https://storage.googleapis.com/${bucket}/${key}`
        )
        .then((getUrl: string) => {
          evidence.push(getUrl);
        })
        .catch((error: Error) => {
          console.warn(error.toString());
        });
    }

    const icannDomain = okayDomain(hostname, true)!;
    const pslDomain = okayDomain(hostname, false)!;

    const actors = new Map<string, { url: string; tld: string }>();

    for (const domain of [/*icannDomain,*/ pslDomain]) {
      const tld = domain.replace(/^[^.]+\./, "");
      actors.set(domain, { url: report.actor.url, tld });
    }

    // console.log({
    //   _id,
    //   ts: new Date(),
    //   realmId: report.reporter.realmId,
    //   token: "n/a",
    //   client: {
    //     code: report.client,
    //     name: config.name,
    //   },
    //   abuse: {
    //     ongoing: true,
    //     class: report.abuse.type!.class,
    //     type: report.abuse.type!.type,
    //     subtype: report.abuse.type!.subType,
    //     // group (n/a)
    //     // type (type.id)
    //     // name (type.type)
    //     date: report.abuse.date,
    //     description: report.abuse.description,
    //     ...report.details,
    //   },
    //   reporter: {
    //     email: report.reporter.email.trim().toLocaleLowerCase(),
    //     name: (report.reporter.fullName || '').trim(),
    //     phone: (report.reporter.phoneNumber || '').trim(),
    //     ip,
    //   },
    //   actors: [...actors].map(([domain, data]) => ({ domain, ...data })),
    //   version: 2,
    //   source: window.location.href,
    //   submit: true,
    //   evidence,
    //   success: false,
    //   captcha,
    // })

    // return;
    const [okay, error] = await mongoClient!
      .db("v5")
      .collection("clientAbuseReports")
      .insertOne({
        _id,
        ts: new Date(),
        realmId: report.reporter.realmId,
        token: "n/a",
        client: {
          code: report.client,
          name: config.name,
        },
        abuse: {
          ongoing: true,
          class: report.abuse.type!.class,
          type: report.abuse.type!.type,
          subtype: report.abuse.type!.subType,
          // group (n/a)
          // type (type.id)
          // name (type.type)
          date: report.abuse.date,
          description: report.abuse.description,
          ...report.details,
        },
        reporter: {
          email: report.reporter.email.trim().toLocaleLowerCase(),
          name: (report.reporter.fullName || '').trim(),
          phone: (report.reporter.phoneNumber || '').trim(),
          ip,
        },
        actors: [...actors].map(([domain, data]) => ({ domain, ...data })),
        version: 2,
        source: window.location.href,
        submit: true,
        evidence,
        success: false,
        captcha,
      })
      .then(() => [true])
      .catch((error) => [false, error.message || error.toString()]);

    setReceipt({ okay, error, _id });
    setSubmitting(false);
  }, [report, hasErrors, mongoClient]);

  const updateReporterName = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.reporter.fullName = event.target.value;
    })
    , [setReport]);

  const updateEmail = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.reporter.email = event.target.value;
    })
    , [setReport]);

  const updateReporterPhone = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.reporter.phoneNumber = event.target.value;
    })
    , [setReport]);

  const updateUrl = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.actor.url = event.target.value;
    })
    , [setReport]);

  const updatePorkbunHosting = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setPorkbunHosting(event.target.value)
    , [setPorkbunHosting]);

  const updateCopyright = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.details.copyrightHolder = event.target.value;
    })
    , [setReport]);

  const updateCopyrightDescription = useCallback((event: ChangeEvent<HTMLTextAreaElement>) =>
    setReport((draft) => {
      draft.details.copyrightDescription = event.target.value;
    })
    , [setReport]);

  const updateDescription = useCallback((event: ChangeEvent<HTMLTextAreaElement>) =>
    setReport((draft) => {
      draft.abuse.description = event.target.value;
    })
    , [setReport]);

  const updateSignature = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.details.copyrightSignature = event.target.value;
    })
    , [setReport]);

  const updateTarget = useCallback((event: ChangeEvent<HTMLInputElement>) =>
    setReport((draft) => {
      draft.details.target = event.target.value;
    })
    , [setReport]);

  useEffect(() => {
    const type = report.abuse.type?.id;

    const email = report.reporter.email;
    const okayEmail = Boolean(
      email.match(/^[^@]+@(?:[\w-]+\.)+[^\W_]{2,}$/) &&
      okayDomain(email.replace(/^[^@]*@/, ""))
    );

    const actor = report.actor.url;
    const okayAbuseActor = okayActor(actor);

    const date = report.abuse.date;
    const okayDate = Boolean(
      date && date instanceof Date && !isNaN(date.getTime())
    );
    const futureDate = Boolean(
      date && date instanceof Date && date.getTime() > Date.now()
    );

    const desc = report.abuse.description;
    const okayDesc = desc.length > 10;

    const target = report.details.target || "";
    const okayTarget = type !== "phishing" || target.length > 1;

    const body = report.details.body || "";
    const okayBody =
      (type !== "spam" && type !== "sms" && type !== "comment") ||
      body.length > 1;

    const url = report.details.url || "";
    const okayUrl = type !== "comment" || okayActor(url);

    const domain = okayAbuseActor ? new URL(actor.replace(/^(?!https?:\/\/)/, 'https://')).hostname : "";
    const ourDomain = (config.type === 'registry' && config.abuseForm.global.restrict) ? Boolean(config.monitor.some((monitor) => domain.endsWith('.' + monitor))) : true;

    const evidence = report.evidence;
    const okayEvidence =
      report.abuse.type?.id === "csam" || evidence.length > 0;

    const fullName = report.reporter.fullName;
    const okayFullName = (fullName?.length ?? 0) > 1;

    const phoneNumber1 = phone(report.reporter.phoneNumber || "", { validateMobilePrefix: false })
    const phoneNumber2 = phone('+' + report.reporter.phoneNumber || "", { validateMobilePrefix: false })
    const okayPhoneNumber = phoneNumber1.isValid || phoneNumber2.isValid;

    const copyrightHolder = report.details.copyrightHolder;
    const okayCopyrightHolder = (copyrightHolder?.length ?? 0) > 1;

    const copyrightDescription = report.details.copyrightDescription;
    const okayCopyrightDescription = (copyrightDescription?.length ?? 0) > 10;

    const copyrightAcknowledgement = report.details.copyrightAcknowledgement;
    const okaycopyrightAcknowledgement = copyrightAcknowledgement === true;

    const copyrightSworn = report.details.copyrightSworn;
    const okayCopyrightSworn = copyrightSworn === true;

    const copyrightSignature = report.details.copyrightSignature;
    const okaySignature = Boolean(copyrightSignature) && fullName === copyrightSignature;

    setErrors((draft) => {
      draft.email =
        !okayEmail && "This does not look like a valid email address";
      draft.actor = (!okayAbuseActor && "This does not look like a valid URL") || (!ourDomain && `This domain is outside the jurisdiction of ${config.name}.`);
      draft.date = !okayDate && "This does not look like a valid date";
      draft.date = futureDate && "This date is in the future";
      draft.description = !okayDesc && "This description is too short";
      draft.target =
        !okayTarget && "Phishing reports must have a target provided";
      draft.url = !okayUrl && "Comment spam reports must have a URL provided";
      draft.body =
        !okayBody && "Spam reports must have a message body provided";
      draft.evidence = !okayEvidence && !(config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "You must provide evidence";
      draft.fullName = !okayFullName && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "You must provide your full name";
      draft.phoneNumber = !okayPhoneNumber && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "This does not look like a valid phone number";
      draft.copyrightHolder = !okayCopyrightHolder && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "The copyright holder name is missing";
      draft.copyrightDescription = !okayCopyrightDescription && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "The copyright description is too short";
      draft.copyrightAcknowledgement = !okaycopyrightAcknowledgement && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "You must acknowledge the 512(f) Acknowledgment, Good Faith Belief and Authority to Act";
      draft.copyrightSignature = !okaySignature && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "Your signature must match your full name";
      draft.copyrightSworn = !okayCopyrightSworn && (config.code === 'porkbun' && report.abuse.type?.id === 'dmca') && "You must swear that the information you are providing is accurate";
    });
  }, [report]);

  const params = useParams();
  const type = params.type as string | undefined;

  const phase = report.phase;
  const nextPhase = useCallback(() => {
    setReport((draft) => {
      draft.phase++;
    });
  }, [setReport]);

  useEffect(() => {
    const reportType = localAbuseTypes.find(({ id }) => id === type);
    if (!type || !reportType) {
      setReport(initialReport);
    } else {
      setReportType(reportType);
    }
  }, [type]);

  const theme = "light";
  const code = config.code;
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(true);
  const clearLogoError = useCallback(() => setLogoError(false), [setLogoError]);
  const exampleTld = useMemo(() => {
    if (config.type === "registry") {
      return config.monitor[0];
    } else {
      return "com";
    }
  }, [config.type, config.monitor]);

  const abuseGroups = useMemo<AbuseGroups[]>(() => {
    const groups = [
      {
        heading: "Inappropriate Content",
        preText: "Examples of inappropriate content include:",
        examples: [
          <>
            Violent or disturbing imagery,{" "}
            <b>
              <i>EXCLUDING</i>
            </b>{" "}
            Child Sexual Abuse Materials (CSAM)
          </>,
          "Content that displays personal information such as social security numbers or credit card numbers",
          config.abuseForm.en.csam
            ? "Content that promotes, encourages, or engages in the sexual abuse and/or exploitation of children (CSAM)"
            : false,
        ].filter(Boolean),
        postText: config.abuseForm.en.csam ? (
          ""
        ) : (
          <>
            To report a web site distributing Child Sexual Abuse Materials
            (CSAM), please go directly to the{" "}
            <Anchor target="_blank" href="https://www.iwf.org.uk/en/uk-report/">
              Internet Watch Foundation
            </Anchor>
          </>
        ),
        types: [
          config.abuseForm.en.csam ? "csam" : "nocsam",
          "violence",
          "pii",
        ].filter(Boolean),
      },
      {
        heading: "Malware",
        preText: "Examples of malware include:",
        examples: [
          `Viruses`,
          `Malicious code`,
          `Corrupt files`,
          `Any other material intended to compromise a user's computer`,
        ],
        postText: "",
        types: ["malware"],
      },
      {
        heading: "Phishing",
        preText:
          "Web sites, emails, or messages requesting confidential personal information.",
        examples: [
          "Phishing is the use of fraudulent communication and/or a fraudulent web site to steal personal information",
        ],
        postText: "",
        types: ["phishing"],
      },
      {
        heading: "Spam",
        preText: "Spam can include repeated, unsolicited:",
        examples: [
          "Emails",
          "Text messages (SMS)",
          "Comments on social media, blogs, or forums",
        ],
        postText:
          "If you are reporting an unsolicited online comment, report the web site contained within the comment, not the web site hosting the comment",
        types: ["spam", "sms", "comment"],
      },
      {
        heading: "Copyright and Trademark Claims",
        preText: "Report the misuse of copyrighted or trademarked material:",
        examples: [
        ],
        postText:
          "If the material is being used within the context of phishing, you will be redirected to the phishing report form.",
        types: [],
      },
    ];

    if (config.abuseTypes) {

      for (const type of config.abuseTypes) {
        const group = groups.find((g) => g.heading === type.heading)!;
        group.types.push(type.name);
        group.examples.push(type.description);

        setLocalAbuseTypes((prev) => [
          ...prev,
          {
            id: type.name,
            class: "Content",
            type: type.xarf,
            label: type.label,
          },
        ]);
      }
    }

    return groups.filter((g) => g.types.length > 0);
  }, [config.abuseForm, config.abuseTypes]);

  const fallbackLogo = useCallback(() => {
    setLogoError(true);
    if (logo.match(/_(light|dark)\.png$/)) {
      setLogo(logo.replace(/_(light|dark)\.png$/, ".png"));
    } else {
      setLogo("");
    }
  }, [theme, code, logo, setLogo]);

  useEffect(() => {
    setLogo(
      `https://storage.googleapis.com/cleandns_public_images_and_logos/logos/${code}_${theme}.png`
    );
  }, [code, theme]);

  const handlePhishingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTrademarkOption(event.target.value);
    if (event.target.value === 'Yes') {
      navigate('/porkbun/phishing');
    }
  };

  if (!user) return <LoadingIndicator />;

  return (
    <Page kind="wide" responsive>
      {(config.abuseForm.en.stickyHeader && !isEmbedded) ? (
        <Header className="header">
          {logoError && (
            <Box justify="center">
              <Text>{config.name} Abuse Reporting Center</Text>
            </Box>
          )}
          {logo && (
            <Image
              src={logo}
              style={{ display: logoError ? "none" : "inline" }}
              height={config.abuseForm.theme?.headerLogoHeight || "32px"}
              alt={`${config.name} logo`}
              onLoad={clearLogoError}
              onError={fallbackLogo}
            />
          )}
          {!logoError && <Text>Abuse Center</Text>}
          <Box flex />
          <Button
            primary
            label={
              mode === "report" ? "Check Report Status" : "Submit a New Report"
            }
            onClick={() => setMode(mode === "report" ? "status" : "report")}
          />
        </Header>
      ) : (
        <Box direction="row" pad="small">
          <Box flex />
          <Button
            primary
            label={
              mode === "report" ? "Check Report Status" : "Submit a New Report"
            }
            onClick={() => setMode(mode === "report" ? "status" : "report")}
          />
          <Box flex />
        </Box>
      )}
      {mode === "status" && <CheckStatus />}
      {mode === "report" && (
        <>
          {receipt.okay && receipt._id && (
            <Layer
              onClickOutside={resetReport}
              onEsc={resetReport}
            >
              <Box pad="medium" width="medium" align="center" gap="small">
                <Text weight="bold">Thank you for your abuse report.</Text>
                <Text>Retain this report ID for your records:</Text>
                <Text>{receipt._id.toString()}</Text>
                <Text>
                  You can use this report ID and your email address to get an
                  update on your abuse report by clicking the "Check Report
                  Status" button above.
                </Text>
                <NameValueList pairProps={{ direction: "column" }} gap='small' margin={{ left: 'large' }}>
                  <NameValuePair
                    name={'Reported Domain:'}
                  >
                    {report.actor.url}
                  </NameValuePair>
                  <NameValuePair
                    name={'Time of Report:'}
                  >
                    {new Date().toLocaleString().replace(/:\d\d /, ' ')}
                  </NameValuePair>
                </NameValueList>
                <Button
                  label="Submit Another Report"
                  onClick={resetReport}
                />
              </Box>
            </Layer>
          )}
          {!receipt.okay && receipt.error && (
            <Layer
              onClickOutside={clearError}
              onEsc={clearError}
            >
              <Box pad="medium" width="medium" align="center" gap="small">
                <Text weight="bold">There was an error submitting your abuse report.</Text>
                <Text>{receipt.error}</Text>
                <Button
                  label="Close and Try Again"
                  onClick={clearError}
                />
              </Box>
            </Layer>
          )}
          {!receipt.okay && !report.abuse.type && (
            <>
              <PageContent gap="1rem" style={{ maxWidth: "900px" }}>
                {!config.abuseForm.en.stickyHeader && (
                  <Heading level="3" margin="none">
                    <>
                      {config.abuseForm.en.title ||
                        `Submit an abuse report to ${config.name}`}
                    </>
                  </Heading>
                )}
                <Text size="large">
                  {config.name} takes abuse reports very seriously and the
                  proper handling of your case is important to us. Abuse
                  includes inappropriate content, malware, phishing, and spam.
                  If you are unsure if our abuse policy applies to your
                  complaint, feel free to refer to our{" "}
                  <TOSLink config={config.abuseForm} />.
                </Text>
                <Text size="large">
                  Please provide your actual email address, the full URL of the
                  website you would like to report (example:{" "}
                  <Text as="kbd">{`https://www.exampledomain.${exampleTld}/page5`}</Text>
                  ), and a detailed description of the incident.{" "}
                </Text>
                <Text size="large">
                  To check the status of a previously submitted abuse report,
                  please click the "Check Report Status" button above. For all
                  other domain issues and questions, please reach out to{" "}
                  <CustomerSupport config={config.abuseForm} />.
                </Text>
                <Box direction="row" align="center" gap="small">
                  <StatusWarning color="status-critical" />
                  <Text size="large">
                    Please ensure you choose the correct abuse type for your
                    report. Clearly mis-{wording(config.code, "categorized")} reports may result in the
                    rejection of your report. (E.g. {wording(config.code, "categorizing")} trademark-related
                    incidents as "Violent or Disturbing Imagery")
                  </Text>
                </Box>
                <Accordion margin={{ bottom: "large" }}>
                  {abuseGroups.map((group) => (
                    <AccordionPanel
                      key={group.heading}
                      label={
                        <Box direction="column">
                          <Heading
                            level={4}
                            margin={{ top: "small", bottom: "none" }}
                          >
                            {group.heading}
                          </Heading>
                          <Box
                            margin={{ top: "small", bottom: "small" }}
                            style={{ fontSize: "small" }}
                          >
                            {group.preText && (
                              <Text size="10pt">{group.preText}</Text>
                            )}
                            <ul>
                              {group.examples.map((ex, n) => (
                                <li key={n}>{ex}</li>
                              ))}
                            </ul>
                            {group.postText && (
                              <Text size="small">{group.postText}</Text>
                            )}
                          </Box>
                        </Box>
                      }
                    >
                      <Box
                        direction="row"
                        gap="small"
                        margin={{ bottom: "medium" }}
                        justify="center"
                      >
                        {group.types
                          .map((id) =>
                            localAbuseTypes.find((type) => type.id === id)
                          )
                          .filter(Boolean)
                          .map((type) => (
                            <Button
                              primary
                              key={type!.id}
                              label={type!.label}
                              icon={<Next color="white" size="small" />}
                              reverse
                              size="small"
                              onClick={() => {
                                if (type!.id === "nocsam") {
                                  window.open(
                                    "https://www.iwf.org.uk/en/uk-report/"
                                  );
                                } else {
                                  navigate(`/${config.code}/${type!.id}`);
                                }
                              }}
                            />
                          ))}
                      </Box>
                    </AccordionPanel>
                  ))}
                </Accordion>
              </PageContent>
            </>
          )}
          {report.abuse.type && (
            <>
              <PageContent gap="1rem" style={{ maxWidth: "900px" }}>
                {!config.abuseForm.en.stickyHeader && (
                  <Heading level="3" margin="none">
                    <>
                      {config.abuseForm.en.title ||
                        `Submit an abuse report to ${config.name}`}
                    </>
                  </Heading>
                )}
                {report.abuse.type.id === "violence" && (
                  <Box direction="row" gap="small" align="center">
                    <StatusWarning color="status-critical" />
                    <Text size="large">
                      Reports of Child Sexual Abuse Materials (CSAM) MUST only
                      be reported via the CSAM option. Please DO NOT submit any
                      report of CSAM, nor upload any CSAM evidence, under the
                      category of "Violent or Disturbing Imagery", to ensure
                      these reports are being handled in accordance with
                      applicable law.
                    </Text>
                  </Box>
                )}
                <Box>
                  <Heading
                    level={4}
                    margin={{ top: "small", bottom: "none" }}
                  >
                    Report {report.abuse.type.type} Abuse
                  </Heading>
                </Box>
                {config.code === 'porkbun' && report.abuse.type.id === 'dmca' && (
                  <>
                    <FormElement
                      component={TextInput}
                      required
                      label="Full Name"
                      help="Enter your full name."
                      error={errors.fullName}
                      name="fullName"
                      value={report.reporter.fullName}
                      placeholder="e.g. John Doe"
                      onChange={updateReporterName}
                    />
                    <FormElement
                      component={TextInput}
                      required
                      label="Email Address"
                      help="Enter your email address."
                      error={errors.email}
                      name="email"
                      value={report.reporter.email}
                      placeholder="e.g. me@email.com"
                      onChange={updateEmail}
                    />
                    <FormElement
                      component={TextInput}
                      required
                      label="Phone Number"
                      help="Enter your phone number."
                      error={errors.phoneNumber}
                      name="phoneNumber"
                      value={report.reporter.phoneNumber}
                      placeholder="e.g. 1234567890"
                      onChange={updateReporterPhone}
                    />
                    <FormElement
                      component={RadioButtonGroup}
                      required
                      label="While the domain may be registered at Porkbun, it could be using web hosting services from a third party. Is this domain using Porkbun’s web hosting services?"
                      help="Please select an option."
                      name="porkbunHosting"
                      value={porkbunHosting}
                      error={errors.porkbunHosting}
                      options={porkbunHostingRadioOptions}
                      onChange={updatePorkbunHosting}
                      conditionalText={
                        porkbunHosting === 'No' ?
                          <Text>
                            If you are looking to file a notice of copyright infringement via the method detailed in the Digital Millennium Copyright Act,
                            our understanding is that the terms of the DMCA apply to web hosting companies and ISPs, not registrars.
                            Furthermore, we don't have the ability to remove specific content from the site.
                            In order to have any content removed, you should contact the registrant, and/or the domain's web hosting company.
                          </Text>
                          : undefined
                      }
                    />

                  </>
                )}
                {config.code === "porkbun" && report.abuse.type.id === "trademark" && (
                  <>
                    <FormField>
                      <Box pad="small">
                        <Text>
                          If the trademark complaint is related to the content of the domain, please use the <Link to="../dmca">DMCA/Copyright form</Link>.
                        </Text>
                      </Box>
                    </FormField>
                    <Box>
                      <FormElement
                        component={RadioButtonGroup}
                        required
                        label="Is this domain engaged in phishing?"
                        help="Please select an option."
                        name="trademark"
                        value={phishingOption}
                        options={phishingRadioOptions}
                        onChange={handlePhishingChange}
                        conditionalText={
                          phishingOption === 'No' ?
                            <Text>
                              ICANN rules as well as Porkbun’s own policies prohibit us from arbitrating trademark disputes in domain names.
                              There are however a few remedies we are aware of,
                              including but not limited to the <a href="https://www.icann.org/resources/pages/help/dndr/udrp-en">UDRP</a>{" "}
                              and the <a href="https://www.icann.org/resources/pages/urs-2014-01-09-en">URS</a>.
                              <br />
                              <br />
                              If you'd like to contact the registrant directly, we provide a method.
                              Here is our guide on <a href="https://kb.porkbun.com/article/154-how-to-contact-a-porkbun-registrant">how to contact a Porkbun registrant</a>.
                            </Text>
                            : undefined
                        }
                      />
                    </Box>
                  </>
                )}
                {!(config.code === 'porkbun' && (report.abuse.type.id === "trademark" || report.abuse.type.id === 'dmca')) && (
                  <FormElement
                    component={TextInput}
                    required
                    label="Your Email Address"
                    help={
                      <>
                        Submitting <b>your</b> email address is <b>necessary</b>{" "}
                        for your abuse report to be processed.
                      </>
                    }
                    error={errors.email}
                    name="email"
                    value={report.reporter.email}
                    placeholder="e.g. me@email.com"
                    onChange={updateEmail}
                  />
                )}
                {phase === 1 && (
                  <>
                    <Box
                      direction="row"
                      gap="small"
                      margin={{ bottom: "medium" }}
                      justify="between"
                    >
                      <Button
                        color="status-critical"
                        
                        onClick={resetReport}
                        size="small"
                        label={<Text color="black">Cancel</Text>}
                      />
                      
                      <Button
                        primary
                        disabled={(config.code === 'porkbun' && report.abuse.type.id === 'dmca')
                          ? (Boolean(errors.fullName || errors.email || errors.phoneNumber) || !porkbunHosting || porkbunHosting === "No")
                          : Boolean(errors.email)}
                        onClick={nextPhase}
                        size="small"
                        label="Continue"
                      />
                    </Box>
                  </>
                )}
                {phase > 1 && (config.code === "porkbun" && report.abuse.type.id === "dmca") && (
                  <>
                    <Box>
                      <Heading
                        level={4}
                        margin={{ top: "small", bottom: "none" }}
                      >
                        General Details
                      </Heading>
                    </Box>
                    <FormElement
                      component={TextInput}
                      required
                      label="Copyright Holder Information"
                      help="Information about the legal copyright holder."
                      error={errors.copyrightHolder}
                      name="copyright"
                      value={report.details.copyrightHolder}
                      onChange={updateCopyright}
                    />
                    <FormElement
                      component={TextInput}
                      required
                      label="Infringing URL"
                      help="Enter the complete URL hosting the abusive content."
                      error={errors.actor}
                      name="actor"
                      value={report.actor.url}
                      placeholder={`e.g. https://www.exampledomain.${exampleTld}/page5`}
                      onChange={updateUrl}
                    />
                    <FormElement
                      component={TextArea}
                      error={errors.copyrightDescription}
                      label="Description of Original Work"
                      help="Describe the copyrighted work in full detail, including links to the original work or source material."
                      name="description"
                      value={report.details.copyrightDescription}
                      rows={3}
                      onChange={updateCopyrightDescription}
                    />
                    <FormElement
                      component={TextArea}
                      required
                      error={errors.description}
                      label="Additional Information"  
                      name="description"
                      value={report.abuse.description}
                      rows={3}
                      placeholder="e.g. This web site asked for my credit card number."
                      onChange={updateDescription}
                    />
                    {phase === 2 && (
                      <>
                        <Box
                          direction="row"
                          gap="small"
                          margin={{ bottom: "medium" }}
                          justify="between"
                        >
                          <Button
                            color="status-critical"
                            onClick={resetReport}
                            size="small"
                            label="Cancel"
                          />
                          <Button
                            primary
                            disabled={Boolean(
                              errors.actor || errors.copyrightHolder || Boolean(errors.fullName || errors.email || errors.phoneNumber || errors.description) || !porkbunHosting || porkbunHosting === "No" 
                            )}
                            onClick={nextPhase}
                            size="small"
                            label="Continue"
                          />
                        </Box>
                      </>
                    )}
                  </>
                )}
                {(phase > 1 && !(config.code === "porkbun" && (report.abuse.type.id === "trademark" || report.abuse.type.id === "dmca")))
                  && (
                    <>
                      <Box>
                        <Heading
                          level={4}
                          margin={{ top: "small", bottom: "none" }}
                        >
                          General Details
                        </Heading>
                      </Box>
                      <FormElement
                        component={TextInput}
                        required
                        label="Offending URL"
                        help="Enter the complete URL hosting the abusive content."
                        error={errors.actor}
                        name="actor"
                        value={report.actor.url}
                        placeholder={`e.g. https://www.exampledomain.${exampleTld}/page5`}
                        onChange={updateUrl}
                      />
                      <Box>
                        <FormField
                          required
                          label={<b>Date of Abuse</b>}
                          error={errors.date}
                          help={
                            <small>
                              <i>
                                When was the abusive content found? (MM/DD/YYYY)
                              </i>
                            </small>
                          }
                        >
                          <DateInput
                            size="small"
                            required
                            readOnly
                            name="date"
                            format="mm/dd/yyyy"
                            value={new Date(report.abuse.date).toISOString()}
                            onChange={(ev) =>
                              setReport((draft) => {
                                draft.abuse.date = new Date(ev.value as string);
                              })
                            }
                          />
                        </FormField>
                      </Box>
                      <FormElement
                        component={TextArea}
                        required
                        label="Description of the Abusive Activity"
                        error={errors.description}
                        name="description"
                        value={report.abuse.description}
                        rows={3}
                        placeholder="e.g. This web site asked for my credit card number."
                        onChange={updateDescription}
                      />
                      {phase === 2 && (
                        <>
                          <Box
                            direction="row"
                            gap="small"
                            margin={{ bottom: "medium" }}
                            justify="between"
                          >
                            <Button
                              color="status-critical"
                              onClick={resetReport}
                              size="small"
                              label="Cancel"
                            />
                            <Button
                              primary
                              disabled={Boolean(
                                errors.actor || errors.date || errors.description
                              )}
                              onClick={nextPhase}
                              size="small"
                              label="Continue"
                            />
                          </Box>
                        </>
                      )}
                    </>
                  )}
                {(phase > 2 && !(config.code === "porkbun" && report.abuse.type.id === "trademark")) && (
                  <>
                    <Box>
                      <Heading
                        level={4}
                        margin={{ top: "small", bottom: "none" }}
                      >
                        Specific {report.abuse.type.type} Details
                      </Heading>
                    </Box>
                    {config.code === "porkbun" && report.abuse.type.id === "dmca" && (
                      <>
                        <FormField
                          label="512(f) Acknowledgment, Good Faith Belief and Authority to Act"
                          help={
                            <Text size="small" color="#4a4a4a">
                              By checking this box, you attest, under penalty of perjury, that you have a good faith belief that use of the material in this report is not authorized by the copyright owner, its agent, or the law; AND you are authorized to act on behalf of the copyright owner; AND you understand, under 17 U.S.C. § 512(f), you may be liable for any damages, including costs and attorneys' fees, if you knowingly materially misrepresent reported material.
                            </Text>
                          }
                          error={errors.copyrightAcknowledgement}
                          required
                        >
                          <CheckBox
                            label="I understand and agree"
                            checked={report.details.copyrightAcknowledgement}
                            onChange={(event) =>
                              setReport((draft) => {
                                draft.details.copyrightAcknowledgement = event.target.checked;
                              })
                            }
                          />
                        </FormField>
                        <FormElement
                          component={TextInput}
                          required
                          label="Digital Signature"
                          help={
                            <Text size="small" color="#4a4a4a">
                              By entering your name, you affirm all information is true and accurate while agreeing to the policies on this page.
                            </Text>
                          }
                          error={errors.copyrightSignature}
                          name="signature"
                          value={report.details.copyrightSignature}
                          placeholder="e.g. John Doe"
                          onChange={updateSignature}
                        />
                        <FormField
                          label="Sworn Statement"
                          help={
                            <Text size="small" color="#4a4a4a">
                              By continuing you declare under penalty of perjury under the laws of the State of Oregon in the United States of America and your own legal jurisdiction that the information being submitted is true and correct.
                            </Text>
                          }
                          error={errors.copyrightSworn}
                          required
                        >
                          <CheckBox
                            label="I understand and agree"
                            checked={report.details.copyrightSworn}
                            onChange={(event) =>
                              setReport((draft) => {
                                draft.details.copyrightSworn = event.target.checked;
                              })
                            }
                          />
                        </FormField>
                      </>
                    )}
                    {report.abuse.type.id === "phishing" && (
                      <>
                        <FormElement
                          component={TextInput}
                          required
                          label="Brand or Company Being Imitated"
                          error={errors.target}
                          name="target"
                          value={report.details.target || ""}
                          placeholder="e.g. Amazon"
                          onChange={updateTarget}
                        />
                        <Box>
                          <FormField
                            label={<b>Email Message Headers</b>}
                            help={
                              <small>
                                <i>
                                  If you received a phishing email and can find
                                  the message headers, paste them here.
                                </i>
                              </small>
                            }
                          >
                            <TextArea
                              name="headers"
                              size="small"
                              value={report.details.headers || ""}
                              // placeholder="e.g. Amazon"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.headers = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            label={<b>Email Message Body</b>}
                            help={
                              <small>
                                <i>
                                  If you received a phishing email, paste the
                                  message body here.
                                </i>
                              </small>
                            }
                          >
                            <TextArea
                              name="body"
                              size="small"
                              value={report.details.body || ""}
                              // placeholder="e.g. Amazon"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.body = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                      </>
                    )}
                    {report.abuse.type.id === "malware" && (
                      <>
                        <Box>
                          <FormField
                            label={<b>Malware Filename</b>}
                            help={
                              <small>
                                <i>
                                  If you know the filename of the malware,
                                  provide it here.
                                </i>
                              </small>
                            }
                          >
                            <TextInput
                              size="small"
                              name="filename"
                              value={report.details.filename || ""}
                              placeholder="e.g. cashappp.exe"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.filename = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            label={<b>Malware or Virus Name</b>}
                            help={
                              <small>
                                <i>
                                  If you know the technical designation of the
                                  malware or virus, provide it here.
                                </i>
                              </small>
                            }
                          >
                            <TextInput
                              size="small"
                              name="target"
                              value={report.details.malware || ""}
                              placeholder="e.g. TROJ_CRIBIT.B"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.malware = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            label={<b>MD5 Hash</b>}
                            help={
                              <small>
                                <i>
                                  If you know the MD5 checksum of the malware,
                                  provide it here.
                                </i>
                              </small>
                            }
                          >
                            <TextInput
                              size="small"
                              name="md5"
                              value={report.details.md5 || ""}
                              placeholder="e.g. 7215ee9c7d9dc229d2921a40e899ec5f (32 hexademical characters)"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.md5 = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            label={<b>SHA1 Hash</b>}
                            help={
                              <small>
                                <i>
                                  If you know the SHA1 checksum of the malware,
                                  provide it here.
                                </i>
                              </small>
                            }
                          >
                            <TextInput
                              size="small"
                              name="target"
                              value={report.details.sha1 || ""}
                              placeholder="e.g. b858cb282617fb0956d960215c8e84d1ccf909c6 (40 hexademical characters)"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.sha1 = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                      </>
                    )}
                    {report.abuse.type.id === "spam" && (
                      <>
                        <Box>
                          <FormField
                            label={<b>Email Message Headers</b>}
                            help={
                              <small>
                                <i>
                                  If you can find the message headers, paste
                                  them here.
                                </i>
                              </small>
                            }
                          >
                            <TextArea
                              size="small"
                              name="headers"
                              value={report.details.headers || ""}
                              // placeholder="e.g. Amazon"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.headers = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            required
                            label={<b>Email Message Body</b>}
                            help={
                              <small>
                                <i>Paste the message body here.</i>
                              </small>
                            }
                          >
                            <TextArea
                              required
                              size="small"
                              name="body"
                              value={report.details.body || ""}
                              // placeholder="e.g. Amazon"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.body = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                      </>
                    )}
                    {report.abuse.type.id === "sms" && (
                      <>
                        <Box>
                          <FormField label={<b>Sender of SMS</b>}>
                            <TextInput
                              size="small"
                              name="target"
                              value={report.details.smsNumber || ""}
                              placeholder="e.g. 1-212-555-1234"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.smsNumber = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            required
                            label={<b>SMS Message</b>}
                            help={
                              <small>
                                <i>Paste the message body here.</i>
                              </small>
                            }
                          >
                            <TextArea
                              required
                              size="small"
                              name="body"
                              value={report.details.body || ""}
                              // placeholder="e.g. Amazon"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.body = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                      </>
                    )}
                    {report.abuse.type.id === "comment" && (
                      <>
                        <Box>
                          <FormField
                            required
                            label={<b>URL of Unsolicited Comment</b>}
                            help={
                              <small>
                                <i>
                                  This is the web site where the comment
                                  appeared.
                                </i>
                              </small>
                            }
                          >
                            <TextInput
                              required
                              size="small"
                              name="url"
                              value={report.details.url || ""}
                              placeholder="e.g. https://twitter.com/CheapGold/status/1283918203981203"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.url = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                        <Box>
                          <FormField
                            required
                            label={<b>Message Body</b>}
                            help={
                              <small>
                                <i>Paste the message body here.</i>
                              </small>
                            }
                          >
                            <TextArea
                              required
                              size="small"
                              name="body"
                              value={report.details.body || ""}
                              // placeholder="e.g. Amazon"
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.body = event.target.value;
                                })
                              }
                            />
                          </FormField>
                        </Box>
                      </>
                    )}
                    {report.abuse.type.id === "csam" ? (
                      <Box margin="1rem">
                        <Text>
                          Due to the sensitive nature of CSAM content, no
                          screenshots or other evidence is permitted to be
                          uploaded with your abuse report.
                        </Text>
                      </Box>
                    ) : (
                      config.code === "porkbun" && report.abuse.type.id === "dmca" ? null :
                        <Box>
                          <FormField
                            label={<b>Evidence of the Abusive Activity</b>}
                            error={errors.evidence}
                            help={
                              <small>
                                <i>
                                  Attach screenshots of the abusive web site or
                                  emails to verify your report.
                                </i>
                              </small>
                            }
                          >
                            <FileInput
                              name="evidence"
                              maxSize={1024 * 1024 * 32} // 32 MB
                              multiple
                              onChange={(event) => {
                                (async function () {
                                  const images = event!.target.files || [];
                                  const acceptable: File[] = [];

                                  for (const image of images) {
                                    const base64 = await fileToBase64(image);
                                    const response = await user!.functions[
                                      "checkImage"
                                    ](base64);

                                    if (
                                      response.adult >= 3 ||
                                      response.racy >= 4
                                    ) {
                                      alert(
                                        `The image ${image.name} appears to contains adult content and is not acceptable for upload.`
                                      );
                                    } else {
                                      acceptable.push(image);
                                    }
                                  }

                                  setReport((draft) => {
                                    // if (acceptable) {
                                    draft.evidence = [
                                      ...draft.evidence,
                                      ...acceptable,
                                    ];
                                    // }
                                  });
                                })();
                              }}
                            />
                          </FormField>
                        </Box>
                    )}
                    <Box
                      direction="row"
                      gap="small"
                      margin={{ bottom: "medium" }}
                      justify="between"
                    >
                      <Button
                        color="status-critical"
                        onClick={resetReport}
                        size="small"
                        label="Cancel"
                      />
                      <Button
                        primary
                        disabled={hasErrors}
                        busy={submitting}
                        onClick={submitReport}
                        size="small"
                        label="Submit"
                      />
                    </Box>
                  </>
                )}
              </PageContent>
            </>
          )}
        </>
      )}
      {/* <Text as="pre">{JSON.stringify(report, null, 4)}</Text> */}
      {!config.abuseForm.en.whiteLabeled && (
        <Box flex direction="row" justify="around">
          <Text>
            powered by{" "}
            <Anchor target="_blank" href="https://cleandns.com">
              CleanDNS
            </Anchor>
          </Text>
        </Box>
      )}
    </Page>
  );
}

function CustomerSupport({ config }: { config: AbuseFormConfig }) {
  const langs = Object.keys(config).filter((lang) => lang.length === 2);
  const [lang] = langs;
  if (langs.length === 1) {
    return (
      <Anchor target="_blank" href={config[lang].support}>
        customer support
      </Anchor>
    );
  } else {
    return (
      <>
        customer support{" "}
        {langs.map((lang) => (
          <React.Fragment key="lang">
            {" "}
            <Anchor target="_blank" key={lang} href={config[lang].support}>
              <FlagEmoji lang={lang} />
            </Anchor>
          </React.Fragment>
        ))}
      </>
    );
  }
}

function TOSLink({ config }: { config: AbuseFormConfig }) {
  const langs = Object.keys(config).filter((lang) => lang.length === 2);
  const [lang] = langs;
  if (langs.length === 1) {
    return (
      <Anchor target="_blank" href={config[lang].link}>
        {config[lang].linkText}
      </Anchor>
    );
  } else {
    return (
      <>
        {config[lang].linkText}{" "}
        {langs.map((lang) => (
          <React.Fragment key="lang">
            {" "}
            <Anchor target="_blank" key={lang} href={config[lang].link}>
              <FlagEmoji lang={lang} />
            </Anchor>
          </React.Fragment>
        ))}
      </>
    );
  }
}

function FlagEmoji({ lang }: { lang: string }) {
  return (
    <Image
      style={{ height: "1rem" }}
      title={`${lang} flag`}
      alt={`[${lang}]`}
      src={`https://storage.googleapis.com/cleandns_public_images_and_logos/flags/${lang}.png`}
    />
  );
}

async function fileToBase64(file: File) {
  return new Promise((pass, fail) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = () =>
      pass(
        fr.result?.toString().replace(/^data:image\/[^;]+;base64,/, "") || ""
      );
    fr.onerror = (error) => fail(error);
  });
}

function wording (client: string, word: string) {
  if (client === 'nominet') {
    if (word === 'categorized') return 'categorised';
    if (word === 'categorizing') return 'categorising';
  }

  return word;
}